import React, { useState } from 'react';
import { Button, Center, Group, Paper, PasswordInput, Stack, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { inject, observer } from 'mobx-react';
import { IconLock } from '@tabler/icons-react';
import * as z from 'zod';
import { t } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostChangePasswordMutation } from '../../../global/entities/Login/mutations/usePostChangePasswordMutation.tsx';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { usePostResetPasswordMutation } from '../../../global/entities/Login/mutations/usePostResetPasswordMutation.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';

const ChangePassword: React.FC = inject()(
    observer(() => {
        const [isSubmitting, setIsSubmitting] = useState(false);
        const postChangePasswordMutation = usePostChangePasswordMutation();
        const postResetPasswordMutation = usePostResetPasswordMutation();
        const navigate = useNavigate();
        const location = useLocation();
        const { currentUser } = useSessionProvider();

        // 🔎 Extraire le token et l'userID de l'URL
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        const userID = searchParams.get('userID');

        const pwRegex = z
            .string()
            .min(8, { message: t('formError.passwordMustBeAtLeast8CharactersLong') })
            .regex(/[a-z]/, { message: t('formError.passwordMustContainLowercase') })
            .regex(/[A-Z]/, { message: t('formError.passwordMustContainUppercase') })
            .regex(/[#!?@$%^&*\-_]/, { message: t('formError.passwordMustContainSpecialCharacter') });

        const schema = z
            .object({
                oldPassword: token ? z.string().optional() : pwRegex,
                newPassword: pwRegex,
                confirmPassword: z.string().min(1, { message: t('formError.confirmPasswordIsRequired') }),
            })
            .refine((data) => data.newPassword === data.confirmPassword, {
                path: ['confirmPassword'],
                message: t('formError.passwordsDoNotMatch'),
            });

        const form = useForm({
            mode: 'uncontrolled',
            initialValues: {
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
            },
            validate: zodResolver(schema),
        });

        const handleSubmit = async (values: { oldPassword: string; newPassword: string; confirmPassword: string }) => {
            setIsSubmitting(true);

            try {
                if (token) {
                    await postResetPasswordMutation.mutateAsync({
                        userID: Number(userID),
                        token: token,
                        newPassword: values.newPassword,
                    });
                } else {
                    await postChangePasswordMutation.mutateAsync({
                        oldPassword: values.oldPassword,
                        newPassword: values.newPassword,
                    });
                }
            } catch (e) {
                if (e instanceof AppMedError) 
                    console.error(e.message);
                 else 
                    console.error('An unexpected error occurred.');
                
            } finally {
                setIsSubmitting(false);
                onClickCancel();
            }
        };

        const onClickCancel = () => {
            if (token) 
                navigate('/login');
             else 
                navigate(currentUser?.redirect || '/login');
            
        };

        return (
            <Center>
                <Paper mt={'lg'} maw={350}>
                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <Stack w={'100%'} gap={'md'}>
                            <Title order={4}>
                                {token ? t('modal.forgot.forgotPasswordTitle') : t('formChangePassword.changePassword')}
                            </Title>

                            {!token && (
                                <PasswordInput
                                    label={t('formChangePassword.oldPassword')}
                                    placeholder={t('formChangePassword.oldPassword')}
                                    leftSection={<IconLock size={16} />}
                                    {...form.getInputProps('oldPassword')}
                                    withAsterisk
                                />
                            )}

                            <PasswordInput
                                label={t('formChangePassword.newPassword')}
                                placeholder={t('formChangePassword.newPassword')}
                                leftSection={<IconLock size={16} />}
                                {...form.getInputProps('newPassword')}
                                withAsterisk
                            />

                            <PasswordInput
                                label={t('formChangePassword.confirmPassword')}
                                placeholder={t('formChangePassword.confirmPassword')}
                                leftSection={<IconLock size={16} />}
                                {...form.getInputProps('confirmPassword')}
                                withAsterisk
                            />

                            <Group gap={0}>
                                <Button fullWidth mt="sm" type="submit" loading={isSubmitting}>
                                    {t('formChangePassword.changePassword')}
                                </Button>
                                <Button
                                    fullWidth
                                    mt="sm"
                                    variant={'default'}
                                    type={'button'}
                                    color={'gray'}
                                    loading={isSubmitting}
                                    onClick={onClickCancel}
                                >
                                    {t('button.cancel')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Paper>
            </Center>
        );
    }),
);

export default ChangePassword;
